import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Warehouse } from 'lucide-react';
import { FilterableSelect } from '../ui/FilterableSelect';
import {
  selectCurrentWarehouse,
  selectWarehouse,
  selectWarehouses,
} from '../../features/warehouseContext/warehouseContextSlice';
import { selectHasWmsAccess } from '../../features/auth/authSelectors';
import { warehouseSubtitle } from '../../utils/inventoryLabels';

const ALL_WAREHOUSES = 'all';

/**
 * The warehouse being worked in, in the top bar of the warehouse module.
 *
 * It is the context of the session, not a filter of a screen: chosen here once,
 * every screen of the module inherits it.
 *
 * With a single warehouse there is nothing to choose and it is only named.
 * Before the warehouses are known, or with none in reach, it draws nothing —
 * `WarehouseScope` is what says so, where there is room to say it.
 *
 * @param {object} props
 * @param {boolean} [props.showClients=true] - Whether a distribution centre is
 *   described by how many clients it has. False for a client: the server only
 *   sends it its own contract, so the count would always read "1 cliente".
 */
export const WarehouseSwitcher = ({ showClients = true }) => {
  const dispatch = useDispatch();
  const hasAccess = useSelector(selectHasWmsAccess);
  const loaded = useSelector((state) => state.warehouseContext.loaded);
  const warehouses = useSelector(selectWarehouses);
  const current = useSelector(selectCurrentWarehouse);

  if (!hasAccess || !loaded || warehouses.length === 0) return null;

  if (warehouses.length === 1) {
    return (
      <span
        data-testid="warehouse-switcher-single"
        title={warehouseSubtitle(warehouses[0], { showClients })}
        className="flex items-center gap-2 border border-border rounded-xl bg-white dark:bg-[#1a1a1a] px-3 py-2 text-sm font-medium text-ninja-dark dark:text-white max-w-[260px]"
      >
        <Warehouse size={16} className="text-ninja-green flex-shrink-0" />
        <span className="truncate">{warehouses[0].name}</span>
      </span>
    );
  }

  // The list says what each warehouse is — its kind, how many clients, whether
  // anybody can operate in it — and the trigger only its name: a warehouse that
  // nobody can operate in looks exactly like one that works until somebody
  // tries to use it.
  const options = [
    { value: ALL_WAREHOUSES, label: 'Todas mis bodegas' },
    ...warehouses.map((warehouse) => ({
      value: warehouse.id,
      label: `${warehouse.name} — ${warehouseSubtitle(warehouse, { showClients })}`,
      shortLabel: warehouse.name,
    })),
  ];

  return (
    <div data-testid="warehouse-switcher" className="max-w-[280px]">
      <FilterableSelect
        label="Bodega:"
        value={current ? current.id : ALL_WAREHOUSES}
        onChange={(value) => dispatch(selectWarehouse(value === ALL_WAREHOUSES ? null : value))}
        options={options}
        searchPlaceholder="Buscar bodega..."
        panelMinWidth={280}
        testId="warehouse-switcher-select"
      />
    </div>
  );
};
